import React, { useRef, useState } from 'react';
import './FancyButton.css';

type Variant = '3d' | 'flat' | 'outline' | 'text';
type Appearance = '3d' | 'classic';
type Size = 'small' | 'medium' | 'large';

type FancyButtonProps = {
  children?: React.ReactNode;
  variant?: Variant;
  appearance?: Appearance; // <--- NUEVO
  size?: Size;
  backgroundColor?: string;
  color?: string;
  borderColor?: string;
  hoverColor?: string;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  iconOnly?: boolean;
  disabled?: boolean;
  ripple?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  fullWidth?: boolean;
  maxWidth?: string | number;
};

const FancyButton: React.FC<FancyButtonProps> = ({
  children = "Button",
  variant = "3d",
  appearance = "3d",
  size = "medium",
  backgroundColor,
  color,
  borderColor,
  hoverColor,
  startIcon,
  endIcon,
  iconOnly = false,
  disabled = false,
  ripple = true,
  onClick,
  fullWidth,
  maxWidth,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isActive, setIsActive] = useState(false);

  // Utilidad para obtener color con opacidad
  const getRGBA = (hex: string, alpha: number) => {
    if (!hex) return '';
    let c = hex.replace('#', '');
    if (c.length === 3) c = c.split('').map(x => x + x).join('');
    const num = parseInt(c, 16);
    return `rgba(${(num >> 16) & 255},${(num >> 8) & 255},${num & 255},${alpha})`;
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    const button = buttonRef.current;
    if (!button || !ripple) {
      if (onClick) onClick(e);
      return;
    }
    const rippleEl = document.createElement('span');
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    rippleEl.style.width = rippleEl.style.height = `${size}px`;
    rippleEl.style.left = `${x}px`;
    rippleEl.style.top = `${y}px`;
    rippleEl.className = 'rippleAnimation';
    button.appendChild(rippleEl);
    setTimeout(() => {
      rippleEl.remove();
    }, 1200);
    if (onClick) onClick(e);
  };

  // Estilos dinámicos
  let style: React.CSSProperties = {
    background: backgroundColor,
    color: color,
    border: (variant === '3d' || variant === 'outline' || borderColor)
      ? `2px solid ${borderColor || (variant === '3d' ? '#016bd6' : variant === 'outline' ? '#0173e6' : 'none')}`
      : 'none',
    boxSizing: 'border-box',
    width: 'auto',
    maxWidth: 'none',
    minWidth: 0,
  };

  if (fullWidth) {
    style.width = '100%';
    style.display = 'block';
  }
  if (maxWidth) {
    style.maxWidth = maxWidth;
  }

  // Para variante text: solo color de texto en hover, fondo solo en active
  if (variant === 'text') {
    style = {
      ...style,
      '--hover-color': hoverColor || '#00b4d8',
      '--active-bg': hoverColor ? getRGBA(hoverColor, 0.15) : 'rgba(0,180,216,0.15)',
      color: isHovered && hoverColor ? hoverColor : color,
      background: isActive && hoverColor
        ? getRGBA(hoverColor, 0.15)
        : 'transparent',
      border: 'none',
      boxShadow: 'none',
      textShadow: 'none',
      transition: 'color 0.2s, background 0.2s',
    } as React.CSSProperties;
  }

  // Para appearance classic: animación tipo pseudo-elemento, pero respeta todas las variantes y props
  if (appearance === 'classic') {
    style = {
      ...style,
      '--classic-hover': hoverColor || color || '#0ea5e9',
      '--classic-border': borderColor || color || '#0ea5e9',
      '--classic-bg': backgroundColor || 'transparent',
      transition: 'color 0.3s, border-color 0.3s, background 0.3s',
    } as React.CSSProperties;
  }

  const classes = [
    'custom-button',
    `variant-${variant}`,
    `size-${size}`,
    appearance === 'classic' ? 'appearance-classic' : '',
    disabled ? 'disabled' : '',
    iconOnly ? 'icon-only' : '',
  ].join(' ');

  return (
    <button
      ref={buttonRef}
      className={classes}
      style={style}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsActive(false);
      }}

      onMouseDown={() => setIsActive(true)}
      onMouseUp={() => setIsActive(false)}
      disabled={disabled}
    >
      {startIcon && <span className="btn-icon start">{startIcon}</span>}
      {!iconOnly && <span className="btn-label">{children}</span>}
      {endIcon && <span className="btn-icon end">{endIcon}</span>}
    </button>
  );
};

export default FancyButton;
export {FancyButton}